import {
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import React, { memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueries } from 'react-query';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import formLocalizedHelperText from 'utils/formLocalizedHelperText';

export default memo(function HomeSelector({
  formik,
  home,
  initialHome,
  index,
}) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);
  const [objects, blocks, homes] = useQueries([
    {
      queryKey: `/dictionary/objects2${index}`,
      queryFn: async function () {
        const response = await axiosPrivate.get('/dictionary/objects2');
        return response.data.data;
      },
      enabled: !hasError,
      onError: (error) => {
        setHasError(true);
      },
      retry: false,
    },
    {
      queryKey: [`/admin/block/index/${home?.object_id}`, home?.object_id],
      queryFn: async function () {
        const response = await axiosPrivate.get(
          `/admin/block/index/${home?.object_id}`
        );
        return response.data.data;
      },
      enabled: !hasError && !!home?.object_id,
      onError: (error) => {
        setHasError(true);
      },
      retry: false,
    },
    {
      queryKey: [
        `/admin/changehomes/${home?.block_id}`,
        home?.block_id,
        initialHome,
      ],
      queryFn: async function () {
        const response = await axiosPrivate.get(
          `/dictionary/changehomes/${initialHome}?block_id=${home?.block_id}`
        );
        return response.data.data;
      },
      enabled: !hasError && !!home?.block_id && !!initialHome,
      onError: (error) => {
        setHasError(true);
      },
      retry: false,
    },
  ]);

  const getFieldValidation = (formikF) => {
    let a = `homelist.${index}.home_id`.split('.');
    let b = formik[formikF];
    for (let i = 0; i < a.length; i++) {
      if (b?.[a?.[i]]) {
        b = b?.[a?.[i]];
      } else {
        b = false;
      }
    }
    return b;
  };

  return (
    <React.Fragment>
      <Grid item={true} lg={4} sm={6} xs={12}>
        <FormControl
          fullWidth
          color='formColor'
          error={formik.touched.object_id && Boolean(formik.errors.object_id)}
        >
          <InputLabel id='objectId-label'>
            {t('common.fields.objectName')}
          </InputLabel>
          <Select
            labelId='objectId-label'
            id='objectId-select'
            label={t('common.fields.objectName')}
            onChange={(event) => {
              formik.setFieldValue(
                `homelist.${index}.object_id`,
                event.target.value,
                true
              );
              formik.setFieldValue(`homelist.${index}.block_id`, '', true);
              formik.setFieldValue(`homelist.${index}.home_id`, '', true);
            }}
            value={objects.isLoading ? '' : home.object_id}
            color='formColor'
            variant='outlined'
            role='presentation'
            MenuProps={{
              id: 'objectId-select-menu',
              disableScrollLock: true,
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {objects.isLoading ? (
              <div className='circular-progress-box'>
                <CircularProgress size={25} />
              </div>
            ) : objects.data && objects.data.length > 0 ? (
              objects.data.map((item, index) => (
                <MenuItem value={item.id} key={index + 1}>
                  {item.name}
                </MenuItem>
              ))
            ) : (
              <div>
                <span className='no-data-found-wrapper select-box'>
                  <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
                  {t('common.global.noDataFound')}
                </span>
              </div>
            )}
          </Select>
          <FormHelperText
            children={
              <span>
                {formik.touched.object_id && t(formik.errors.object_id)}
              </span>
            }
            error={formik.touched.object_id && Boolean(formik.errors.object_id)}
          />
        </FormControl>
      </Grid>

      <Grid item={true} lg={4} sm={6} xs={12}>
        <FormControl
          fullWidth
          color='formColor'
          error={formik.touched.object_id && Boolean(formik.errors.object_id)}
        >
          <InputLabel id='blockId-label'>
            {t('common.fields.blockName')}
          </InputLabel>
          <Select
            labelId='blockId-label'
            id='blockId-select'
            label={t('common.fields.blockName')}
            onChange={(event) => {
              formik.setFieldValue(
                `homelist.${index}.block_id`,
                event.target.value,
                true
              );
              formik.setFieldValue(`homelist.${index}.home_id`, '', true);
            }}
            value={blocks.isLoading ? '' : home.block_id}
            color='formColor'
            variant='outlined'
            role='presentation'
            disabled={!home.object_id}
            MenuProps={{
              id: 'blockId-select-menu',
              disableScrollLock: true,
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {blocks.isLoading ? (
              <div className='circular-progress-box'>
                <CircularProgress size={25} />
              </div>
            ) : blocks.data && blocks.data.length > 0 ? (
              blocks.data.map((item, index) => (
                <MenuItem value={item.id} key={index + 1}>
                  {item.name}
                </MenuItem>
              ))
            ) : (
              <div>
                <span className='no-data-found-wrapper select-box'>
                  <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
                  {t('common.global.noDataFound')}
                </span>
              </div>
            )}
          </Select>
          <FormHelperText
            children={
              <span>
                {formik.touched.block_id && t(formik.errors.block_id)}
              </span>
            }
            error={formik.touched.block_id && Boolean(formik.errors.block_id)}
          />
        </FormControl>
      </Grid>

      <Grid item={true} lg={4} sm={6} xs={12}>
        <FormControl
          fullWidth
          color='formColor'
          error={formik.touched.object_id && Boolean(formik.errors.object_id)}
        >
          <InputLabel id='home_id-label'>
            {t('common.fields.homeNumber')}
          </InputLabel>
          <Select
            labelId='home_id-label'
            id='home_id-select'
            label={t('common.fields.homeNumber')}
            disabled={!home.block_id}
            onChange={(event) => {
              formik.setFieldValue(
                `homelist.${index}.home_id`,
                event.target.value,
                true
              );
            }}
            value={homes.isLoading ? '' : home?.home_id}
            color='formColor'
            variant='outlined'
            role='presentation'
            MenuProps={{
              id: 'home_id-select-menu',
              disableScrollLock: true,
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {homes?.isLoading ? (
              <div className='circular-progress-box'>
                <CircularProgress size={25} />
              </div>
            ) : homes?.data && homes?.data?.length > 0 ? (
              homes?.data?.map((item, index) => {
                return (
                  <MenuItem value={item?.id} key={index + 1}>
                    <div>
                      №{item?.number}{' '}
                      <span className='text-sm text-gray-600'>
                        (
                        <Trans
                          i18nKey='contract.modal.homeOrder.details'
                          values={{
                            stage: item?.stage,
                            rooms: item?.rooms,
                            square: item?.square,
                          }}
                        >
                          1-qavat, 1 xonali, 10 m<sup>2</sup>
                        </Trans>
                        )
                      </span>
                    </div>
                  </MenuItem>
                );
              })
            ) : (
              <div>
                <span className='no-data-found-wrapper select-box'>
                  <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
                  {t('common.global.noDataFound')}
                </span>
              </div>
            )}
          </Select>
          <FormHelperText
            error={
              getFieldValidation('touched') &&
              Boolean(getFieldValidation('errors'))
            }
            children={
              getFieldValidation('touched') &&
              formLocalizedHelperText(getFieldValidation('errors'))
            }
          />
        </FormControl>
      </Grid>
    </React.Fragment>
  );
});
